/**
 * Normalize problem statement text copied from UIs that mix Unicode + raw LaTeX.
 * Target style: Unicode math (≤, ≥, 10⁴) — readable without a math renderer.
 */

const SUPER = {
  '0': '⁰',
  '1': '¹',
  '2': '²',
  '3': '³',
  '4': '⁴',
  '5': '⁵',
  '6': '⁶',
  '7': '⁷',
  '8': '⁸',
  '9': '⁹',
  '+': '⁺',
  '-': '⁻',
  '−': '⁻',
};

const toSuperscript = (exp) =>
  String(exp)
    .split('')
    .map((ch) => SUPER[ch] || ch)
    .join('');

const normalizeKey = (s) =>
  String(s)
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/[−–—]/g, '-')
    .replace(/\s+/g, '')
    .toLowerCase();

/** Convert common LaTeX / mangled math into Unicode. */
export const sanitizeProblemText = (input) => {
  if (input == null) return '';
  let s = String(input);

  // Keep injected SVG explainers intact (entity decoding would break captions / SVG).
  const visualSlots = [];
  s = s.replace(
    /<!--CH_VISUAL_START-->[\s\S]*?<!--CH_VISUAL_END-->/g,
    (block) => {
      const key = `\uE000CH_VISUAL_${visualSlots.length}\uE000`;
      visualSlots.push(block);
      return key;
    },
  );

  // Decode a few common HTML entities that appear in pasted constraints
  s = s
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&amp;/gi, '&')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&#8722;|&minus;/gi, '−');

  // \text{...}, \mathrm{...}, \mathbf{...} → inner text
  s = s.replace(/\\text\{([^{}]*)\}/g, '$1');
  s = s.replace(/\\mathrm\{([^{}]*)\}/g, '$1');
  s = s.replace(/\\mathbf\{([^{}]*)\}/g, '$1');
  s = s.replace(/\\textit\{([^{}]*)\}/g, '$1');
  s = s.replace(/\\textbf\{([^{}]*)\}/g, '$1');

  // Simple \frac{a}{b}
  s = s.replace(/\\frac\{([^{}]+)\}\{([^{}]+)\}/g, '($1)/($2)');

  // \sqrt{x}
  s = s.replace(/\\sqrt\{([^{}]+)\}/g, '√($1)');

  // Binary / relation operators
  const ops = [
    [/\\leq(?![a-zA-Z])/g, '≤'],
    [/\\le(?![a-zA-Z])/g, '≤'],
    [/\\geq(?![a-zA-Z])/g, '≥'],
    [/\\ge(?![a-zA-Z])/g, '≥'],
    [/\\neq(?![a-zA-Z])/g, '≠'],
    [/\\ne(?![a-zA-Z])/g, '≠'],
    [/\\times(?![a-zA-Z])/g, '×'],
    [/\\cdot(?![a-zA-Z])/g, '·'],
    [/\\infty(?![a-zA-Z])/g, '∞'],
    [/\\pm(?![a-zA-Z])/g, '±'],
    [/\\mp(?![a-zA-Z])/g, '∓'],
    [/\\ldots(?![a-zA-Z])/g, '…'],
    [/\\dots(?![a-zA-Z])/g, '…'],
    [/\\rightarrow(?![a-zA-Z])/g, '→'],
    [/\\leftarrow(?![a-zA-Z])/g, '←'],
    [/\\Rightarrow(?![a-zA-Z])/g, '⇒'],
    [/\\Leftrightarrow(?![a-zA-Z])/g, '⇔'],
    [/\\in(?![a-zA-Z])/g, '∈'],
    [/\\notin(?![a-zA-Z])/g, '∉'],
    [/\\subset(?![a-zA-Z])/g, '⊂'],
    [/\\subseteq(?![a-zA-Z])/g, '⊆'],
    [/\\cup(?![a-zA-Z])/g, '∪'],
    [/\\cap(?![a-zA-Z])/g, '∩'],
    [/\\emptyset(?![a-zA-Z])/g, '∅'],
    [/\\forall(?![a-zA-Z])/g, '∀'],
    [/\\exists(?![a-zA-Z])/g, '∃'],
    [/\\sum(?![a-zA-Z])/g, '∑'],
    [/\\prod(?![a-zA-Z])/g, '∏'],
    [/\\approx(?![a-zA-Z])/g, '≈'],
    [/\\equiv(?![a-zA-Z])/g, '≡'],
  ];
  for (const [re, rep] of ops) s = s.replace(re, rep);

  // Braces / escapes
  s = s.replace(/\\\{/g, '{').replace(/\\\}/g, '}');
  s = s.replace(/\\_/g, '_');
  s = s.replace(/\\%/g, '%');
  s = s.replace(/\\&/g, '&');
  s = s.replace(/\\#/g, '#');
  s = s.replace(/\\,/g, ' ');
  s = s.replace(/\\;/g, ' ');
  s = s.replace(/\\!/g, '');
  s = s.replace(/\\quad/g, '  ');
  s = s.replace(/\\qquad/g, '    ');

  // 10^4, n^2, 2^{10} → superscripts
  s = s.replace(/(\w)\^\{(-?\d+)\}/g, (_, base, exp) => base + toSuperscript(exp));
  s = s.replace(/(\w)\^(-?\d+)/g, (_, base, exp) => base + toSuperscript(exp));

  // Copied UI often turns 10⁴ into "104" next to inequalities: -104≤x≤104
  // Only exponents 4–9 (never 100/101/102/103 — those are normal integers)
  s = s.replace(
    /(?<![\d⁰¹²³⁴⁵⁶⁷⁸⁹])(-?)10([4-9])(?![\d⁰¹²³⁴⁵⁶⁷⁸⁹])(?=≤|≥)/g,
    (_, sign, d) => sign + '10' + toSuperscript(d),
  );
  s = s.replace(
    /(?<=[≤≥])\s*(-?)10([4-9])(?![\d⁰¹²³⁴⁵⁶⁷⁸⁹])/g,
    (_, sign, d) => ' ' + sign + '10' + toSuperscript(d),
  );

  // Leftover $...$ math delimiters
  s = s.replace(/\$\$([\s\S]*?)\$\$/g, '$1');
  s = s.replace(/\$([^$]+)\$/g, '$1');
  s = s.replace(/\\\(([\s\S]*?)\\\)/g, '$1');
  s = s.replace(/\\\[([\s\S]*?)\\\]/g, '$1');

  // Stray backslash commands we didn't map → drop the slash
  s = s.replace(/\\([a-zA-Z]+)/g, '$1');

  // Normalize dashes used as minus in math-ish contexts
  s = s.replace(/–|—/g, '−');

  // Tidy spaces around relations
  s = s.replace(/\s*([≤≥≠≈≡∈∉×·±])\s*/g, ' $1 ');
  s = s.replace(/[ \t]{2,}/g, ' ');

  // Collapse duplicated lines / mashed duplicate constraints
  s = collapseDuplicates(s);

  // Restore protected visual blocks
  visualSlots.forEach((block, i) => {
    s = s.replace(`\uE000CH_VISUAL_${i}\uE000`, block);
  });

  return s.trim();
};

const collapseDuplicates = (text) => {
  // 1) Line-level dedupe (order preserved)
  const lines = text.split(/\r?\n/);
  const seenLine = new Set();
  const uniqueLines = [];
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      if (uniqueLines.length && uniqueLines[uniqueLines.length - 1] !== '') {
        uniqueLines.push('');
      }
      continue;
    }
    const key = normalizeKey(trimmed);
    if (seenLine.has(key)) continue;
    seenLine.add(key);
    uniqueLines.push(trimmed);
  }

  let result = uniqueLines.join('\n');

  // 2) Within each line, collapse consecutive identical constraint chunks
  result = result
    .split('\n')
    .map((line) => collapseMashedConstraints(line))
    .join('\n');

  return result;
};

/**
 * Handles strings like:
 * −10⁴ ≤ array[i] ≤ 10⁴ −10⁴ ≤ array[i] ≤ 10⁴
 * or unicode+latex mashed into one line.
 */
const collapseMashedConstraints = (line) => {
  if (!line || line.length < 12) return line;
  // Don't reshape HTML markup
  if (/<[a-z][\s\S]*>/i.test(line)) return line;

  const chunkRe =
    /(?=(?:^|[\s;])[-−]?[\d⁰¹²³⁴⁵⁶⁷⁸⁹]+(?:\.\d+)?\s*[≤≥]\s*)/g;
  const parts = line.split(chunkRe).map((p) => p.trim()).filter(Boolean);

  if (parts.length <= 1) {
    for (let len = Math.floor(line.length / 2); len >= 10; len--) {
      const a = line.slice(0, len).trim();
      const rest = line.slice(len).trim();
      if (!rest) break;
      if (normalizeKey(a) === normalizeKey(rest.slice(0, a.length))) return a;
      if (normalizeKey(rest).startsWith(normalizeKey(a))) return a;
    }
    return line;
  }

  const seen = new Set();
  const out = [];
  for (const part of parts) {
    const key = normalizeKey(part);
    if (!key || seen.has(key)) continue;
    // Only treat as separate constraints if the chunk has a relation symbol
    if (!/[≤≥]/.test(part) && out.length) {
      out[out.length - 1] = `${out[out.length - 1]} ${part}`.trim();
      continue;
    }
    seen.add(key);
    out.push(part);
  }
  return out.join(out.every((p) => /[≤≥]/.test(p)) ? '\n' : ' ');
};

/** Sanitize then convert newlines/tabs for HTML display. */
export const formatProblemHtml = (input) => {
  const cleaned = sanitizeProblemText(input);
  return cleaned
    .replace(/\n/g, '<br />')
    .replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;');
};

/** True if text still looks like it has LaTeX / mangled math worth cleaning. */
export const needsProblemSanitize = (input) => {
  if (!input) return false;
  const s = String(input);
  return /\\[a-zA-Z]+|\^[\{\d]|\\text\{|\\leq|\\geq|\$\$|\\\(|\\\[[\s\S]|≤.*\\leq|10\^[0-9]/.test(
    s,
  );
};

export default sanitizeProblemText;
